import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../modules/urls'
import { resolveProfileImageDisplayUrl } from '../../modules/profileImageUrl'

export interface OrganizationListItem {
  id: string
  name: string
  role: string
  workspace_user_email?: string
  display_name?: string
  is_active: boolean
  can_delete?: boolean
  billing_summary?: { product: string; interval: string | null } | null
  member_count?: number
  delete_block_reason?: string | null
  logo_url?: string | null
}

export interface OrganizationCreateContext {
  is_first_owned_org: boolean
}

export interface OrganizationDetail {
  id: string
  name: string
  role: string
  workspace_user_email?: string
  billing_summary: { product: string; interval: string | null } | null
  member_count: number
  can_delete: boolean
  delete_block_reason?: string | null
}

export interface OrganizationMemberItem {
  member_unique_user_id: string
  email: string
  display_name: string | null
  role: string
  status: string
}

interface OrganizationsState {
  items: OrganizationListItem[]
  isWorkspaceContext: boolean
  homeUserEmail: string | null
  homeUserDisplayName: string | null
  membersByOrgId: Record<string, OrganizationMemberItem[]>
  membersStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  memberRoleStatus: 'idle' | 'loading' | 'failed'
  transferStatus: 'idle' | 'loading' | 'failed'
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  switchStatus: 'idle' | 'loading' | 'failed'
  error: string | null
}

const initialState: OrganizationsState = {
  items: [],
  isWorkspaceContext: false,
  homeUserEmail: null,
  homeUserDisplayName: null,
  membersByOrgId: {},
  membersStatus: 'idle',
  memberRoleStatus: 'idle',
  transferStatus: 'idle',
  status: 'idle',
  switchStatus: 'idle',
  error: null,
}

export const provisionOrganizationTenancy = createAsyncThunk(
  'organizations/provision',
  async (_, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/provision`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to provision organization tenancy'
      )
    }
    return res.json()
  }
)

export const fetchOrganizations = createAsyncThunk(
  'organizations/fetch',
  async (_, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
    if (!res.ok) {
      return rejectWithValue('Failed to load organizations')
    }
    return res.json()
  }
)

export const switchOrganization = createAsyncThunk(
  'organizations/switch',
  async (organizationId: string, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/switch`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to switch organization'
      )
    }
    return res.json()
  }
)

export const switchToHomeWorkspace = createAsyncThunk(
  'organizations/switchHome',
  async (_, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/switch/home`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!res.ok) {
      return rejectWithValue('Failed to return to personal account')
    }
    return res.json()
  }
)

export interface CreateOrganizationPayload {
  name: string
  plan?: { product: string; interval?: 'month' | 'year' }
  payment?: { stripe_token: string }
  business_price?: number
}

/** Session flag: create-org wizard should resume on integrations after reload (legacy). */
export const CREATE_ORG_WIZARD_RESUME_KEY = 'payhelm:createOrgWizardResume'

export const fetchCreateContext = createAsyncThunk(
  'organizations/fetchCreateContext',
  async (_, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/create-context`, {
      credentials: 'include',
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to load create context'
      )
    }
    return res.json() as OrganizationCreateContext
  }
)

export const fetchOrganizationDetail = createAsyncThunk(
  'organizations/fetchDetail',
  async (organizationId: string, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/${organizationId}`, {
      credentials: 'include',
    })
    if (!res.ok) {
      return rejectWithValue('Failed to load organization')
    }
    const body = await res.json()
    return body.organization as OrganizationDetail
  }
)

export const createOrganization = createAsyncThunk(
  'organizations/create',
  async (payload: CreateOrganizationPayload, { rejectWithValue }) => {
    const trimmed = payload.name.trim()
    if (!trimmed) {
      return rejectWithValue('Organization name is required')
    }
    const res = await fetch(`${baseUrl}/organizations`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: trimmed,
        ...(payload.plan ? { plan: payload.plan } : {}),
        ...(payload.payment ? { payment: payload.payment } : {}),
        ...(payload.business_price != null ? { business_price: payload.business_price } : {}),
      }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to create organization'
      )
    }
    return res.json()
  }
)

export const deleteOrganization = createAsyncThunk(
  'organizations/delete',
  async (organizationId: string, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/${organizationId}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue((body as { error?: string }).error || 'Failed to delete organization')
    }
    return res.json()
  }
)

export const fetchOrganizationMembers = createAsyncThunk(
  'organizations/fetchMembers',
  async (organizationId: string, { rejectWithValue }) => {
    const res = await fetch(`${baseUrl}/organizations/${organizationId}/members`, {
      credentials: 'include',
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to load organization members'
      )
    }
    const body = await res.json()
    return { organizationId, members: (body.members || []) as OrganizationMemberItem[] }
  }
)

export const setOrganizationMemberRole = createAsyncThunk(
  'organizations/setMemberRole',
  async (
    {
      organizationId,
      memberUniqueUserId,
      role,
    }: { organizationId: string; memberUniqueUserId: string; role: 'admin' | 'member' },
    { rejectWithValue }
  ) => {
    const res = await fetch(`${baseUrl}/organizations/${organizationId}/members/role`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_unique_user_id: memberUniqueUserId, role }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to update organization member role'
      )
    }
    return { organizationId }
  }
)

export const transferOrganizationOwnership = createAsyncThunk(
  'organizations/transferOwnership',
  async (
    {
      organizationId,
      memberUniqueUserId,
    }: { organizationId: string; memberUniqueUserId: string },
    { rejectWithValue }
  ) => {
    const res = await fetch(`${baseUrl}/organizations/${organizationId}/transfer-ownership`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_unique_user_id: memberUniqueUserId }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return rejectWithValue(
        (body as { error?: string }).error || 'Failed to transfer organization ownership'
      )
    }
    return res.json()
  }
)

export const renameOrganization = createAsyncThunk(
  'organizations/rename',
  async ({ organizationId, name }: { organizationId: string; name: string }, { rejectWithValue }) => {
    const trimmed = name.trim()
    if (!trimmed) {
      return rejectWithValue('Organization name is required')
    }
    const res = await fetch(`${baseUrl}/organizations/${organizationId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: trimmed }),
    })
    if (!res.ok) {
      return rejectWithValue('Failed to rename organization')
    }
    return res.json()
  }
)

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    patchActiveOrgLogoUrl: (state, action: { payload: { logoUrl: string | null } }) => {
      const active = state.items.find((org) => org.is_active)
      if (active) {
        active.logo_url = resolveProfileImageDisplayUrl(action.payload.logoUrl) ?? action.payload.logoUrl
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        if (state.status === 'idle' || state.status === 'failed') {
          state.status = 'loading'
        }
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.items = (action.payload.organizations || []).map(
          (org: OrganizationListItem) => ({
            ...org,
            logo_url: resolveProfileImageDisplayUrl(org.logo_url) ?? org.logo_url ?? null,
          })
        )
        state.isWorkspaceContext = !!action.payload.is_workspace_context
        state.homeUserEmail = action.payload.home_user_email || null
        state.homeUserDisplayName = action.payload.home_user_display_name || null
      })
      .addCase(fetchOrganizationMembers.pending, (state) => {
        state.membersStatus = 'loading'
      })
      .addCase(fetchOrganizationMembers.fulfilled, (state, action) => {
        state.membersStatus = 'succeeded'
        state.membersByOrgId[action.payload.organizationId] = action.payload.members
      })
      .addCase(fetchOrganizationMembers.rejected, (state) => {
        state.membersStatus = 'failed'
      })
      .addCase(setOrganizationMemberRole.pending, (state) => {
        state.memberRoleStatus = 'loading'
      })
      .addCase(setOrganizationMemberRole.fulfilled, (state) => {
        state.memberRoleStatus = 'idle'
      })
      .addCase(setOrganizationMemberRole.rejected, (state) => {
        state.memberRoleStatus = 'failed'
      })
      .addCase(transferOrganizationOwnership.pending, (state) => {
        state.transferStatus = 'loading'
      })
      .addCase(transferOrganizationOwnership.fulfilled, (state) => {
        state.transferStatus = 'idle'
      })
      .addCase(transferOrganizationOwnership.rejected, (state) => {
        state.transferStatus = 'failed'
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.status = 'failed'
        state.error = (action.payload as string) || action.error.message || 'Error'
      })
      .addCase(provisionOrganizationTenancy.fulfilled, (state, action) => {
        state.items = action.payload.organizations || []
        state.status = 'succeeded'
        state.error = null
      })
      .addCase(provisionOrganizationTenancy.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Provision failed'
      })
      .addCase(switchOrganization.pending, (state) => {
        state.switchStatus = 'loading'
      })
      .addCase(switchOrganization.fulfilled, (state) => {
        state.switchStatus = 'idle'
      })
      .addCase(switchToHomeWorkspace.fulfilled, (state) => {
        state.switchStatus = 'idle'
        state.isWorkspaceContext = false
      })
      .addCase(switchOrganization.rejected, (state) => {
        state.switchStatus = 'failed'
      })
      .addCase(createOrganization.fulfilled, (state) => {
        state.isWorkspaceContext = true
      })
      .addCase(renameOrganization.fulfilled, (state, action) => {
        const updated = action.payload.organization
        if (updated?.id) {
          state.items = state.items.map((org) => {
            if (org.id !== updated.id) return org
            return {
              ...org,
              ...updated,
              name: updated.name,
              display_name: updated.display_name ?? updated.name,
              display_hint:
                updated.display_hint !== undefined ? updated.display_hint : org.display_hint,
              is_active: org.is_active,
            }
          })
        }
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        const deletedId = action.payload.deleted_organization_id as string
        state.items = state.items.filter((org) => org.id !== deletedId)
      })
  },
})

export const { patchActiveOrgLogoUrl } = organizationsSlice.actions

export const selectOrganizations = (state: { organizations: OrganizationsState }) =>
  state.organizations.items
export const selectIsWorkspaceContext = (state: { organizations: OrganizationsState }) =>
  state.organizations.isWorkspaceContext
export const selectOrganizationsStatus = (state: { organizations: OrganizationsState }) =>
  state.organizations.status
export const selectHomeUserEmail = (state: { organizations: OrganizationsState }) =>
  state.organizations.homeUserEmail
export const selectHomeUserDisplayName = (state: { organizations: OrganizationsState }) =>
  state.organizations.homeUserDisplayName
export const selectOrganizationMembers = (orgId: string) => (state: { organizations: OrganizationsState }) =>
  state.organizations.membersByOrgId[orgId] || []
export const selectOrganizationMembersStatus = (state: { organizations: OrganizationsState }) =>
  state.organizations.membersStatus
export const selectTransferOrganizationStatus = (state: { organizations: OrganizationsState }) =>
  state.organizations.transferStatus

export default organizationsSlice.reducer
